import { useMemo } from "react"

import { ROUTES } from "common/constants/routes"
import { FeatureFlag } from "governance/context"
import { useOrganization } from "organization/providers/OrganizationProvider"
import type { Governor } from "query/graphql"
import { useGovernanceCouncilsQuery } from "query/graphql"
import { getAssetId } from "web3/helpers/assetId"
import type { NavigationTab } from "ui/components/NavigationTabs"
import { isStagingEnv } from "common/constants/env"
import {
  getGuardianOrganizationSettings,
  GuardianType,
} from "governance/constants/guardian"
import { subString } from "common/helpers/string"
import { getSecCouncilMembersOrganizationSettings } from "governance/constants/security-council-members"

type Values = {
  tabs: NavigationTab[]
}

const useMembersNavigationTabs = ({
  organizationId,
  slug,
  governor,
}: {
  organizationId: string
  slug: string
  governor: Governor
}): Values => {
  const getAssetIdFromGovernor = (governor: Governor) => {
    if (!governor) {
      return null
    }

    const { contracts, chainId } = governor
    const { tokens } = contracts
    const [token] = tokens
    const { address, type } = token

    const assetId = getAssetId({
      assetName: {
        namespace: type,
        reference: address,
      },
      chainId,
    })

    return assetId
  }

  const assetId = getAssetIdFromGovernor(governor)

  const { isFeatureFlagOn } = useOrganization()

  const { data: councilsData } = useGovernanceCouncilsQuery(
    {
      tokenId: assetId ?? "",
    },
    { enabled: isFeatureFlagOn(FeatureFlag.Council) && Boolean(assetId) },
  )

  const { data: testnetData } = useGovernanceCouncilsQuery(
    {
      tokenId: "eip155:421614/erc20:0xF625EFf2bBa0C3Dfbe17b4bA6436cFa3743a183a",
    },
    { enabled: isStagingEnv },
  )

  const tabs = useMemo(() => {
    // Councils
    const councils = councilsData?.councils.concat(testnetData?.councils ?? [])

    const councilTabs = councils
      ? councils.map((council) => ({
          label: subString(council.name, 20),
          url: ROUTES.governance.council.index(slug, council.slug),
          isActive: true,
          name: council.name,
        }))
      : []

    // Guardians / UAC
    const guardianSafe = getGuardianOrganizationSettings(organizationId)
    const guardiansTabs =
      guardianSafe && guardianSafe.type === GuardianType.Guardian
        ? [
            {
              label: "Guardians",
              url: ROUTES.governance.guardians(slug),
            },
          ]
        : undefined
    const uacTabs =
      guardianSafe && guardianSafe.type === GuardianType.UAC
        ? [
            {
              label: "UAC",
              url: ROUTES.governance.uac(slug),
            },
          ]
        : undefined

    const secCouncilMembers =
      getSecCouncilMembersOrganizationSettings(organizationId)
    const secCouncilMembersTabs = secCouncilMembers
      ? [
          {
            label: "Security Council",
            url: ROUTES.governance.securityCouncilMembers(slug),
          },
        ]
      : undefined

    return [
      {
        label: "Delegates",
        url: ROUTES.governance.delegates(slug),
      },
      ...(guardiansTabs || []),
      ...(uacTabs || []),
      ...(secCouncilMembersTabs || []),
      ...councilTabs,
    ]
  }, [councilsData?.councils, testnetData?.councils, organizationId, slug])

  return {
    tabs,
  }
}

export default useMembersNavigationTabs
